"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useLenis } from "lenis/react";
import { ChevronRight, Grid2X2, Pause, Play, X } from "lucide-react";

export type VideoGalleryItem = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  videoUrl?: string;
  isActive?: boolean;
};

export type VideoModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  videoUrl?: string;
  galleryItems?: VideoGalleryItem[];
  onSelectGalleryItem?: (index: number) => void;
  onPrevious?: () => void;
  onNext?: () => void;
};

type MediaSource =
  | { type: "youtube"; videoId: string }
  | { type: "video"; src: string };

function getYouTubeVideoId(url: string): string | null {
  const trimmed = url.trim();
  const match = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );

  return match?.[1] ?? null;
}

function resolveMediaSource(url?: string): MediaSource | null {
  if (!url) {
    return null;
  }

  const youtubeVideoId = getYouTubeVideoId(url);
  if (youtubeVideoId) {
    return { type: "youtube", videoId: youtubeVideoId };
  }

  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) {
    return { type: "video", src: url };
  }

  return null;
}

function getSelectedIndex(items: VideoGalleryItem[]): number {
  const activeIndex = items.findIndex((item) => item.isActive);
  return activeIndex >= 0 ? activeIndex : 0;
}

function postYouTubeCommand(
  iframe: HTMLIFrameElement | null,
  command: "playVideo" | "pauseVideo",
) {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({
      event: "command",
      func: command,
      args: [],
    }),
    "*",
  );
}

function YouTubeEmbed({
  videoId,
  title,
  isPlaying,
}: {
  videoId: string;
  title: string;
  isPlaying: boolean;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hasMountedRef = useRef(false);
  const origin =
    typeof window !== "undefined" ? window.location.origin : undefined;

  const embedSrc = [
    `https://www.youtube.com/embed/${videoId}`,
    "?autoplay=1",
    "&rel=0",
    "&modestbranding=1",
    "&playsinline=1",
    "&enablejsapi=1",
    origin ? `&origin=${encodeURIComponent(origin)}` : "",
  ].join("");

  useEffect(() => {
    // Skip the first run — autoplay already starts the video on mount.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    postYouTubeCommand(iframeRef.current, isPlaying ? "playVideo" : "pauseVideo");
  }, [isPlaying]);

  useEffect(() => {
    hasMountedRef.current = false;
  }, [videoId]);

  return (
    <iframe
      key={videoId}
      ref={iframeRef}
      src={embedSrc}
      title={title}
      className="absolute inset-0 h-full w-full border-0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}

export function VideoModal({
  open,
  onClose,
  title,
  videoUrl,
  galleryItems = [],
  onSelectGalleryItem,
}: VideoModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const lenis = useLenis();
  const [showSideGallery, setShowSideGallery] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const activeIndex = useMemo(
    () => getSelectedIndex(galleryItems),
    [galleryItems],
  );
  const activeItem = galleryItems[activeIndex];
  const activeMedia = resolveMediaSource(activeItem?.videoUrl ?? videoUrl);
  const activeMediaKey =
    activeMedia?.type === "youtube"
      ? activeMedia.videoId
      : activeMedia?.type === "video"
        ? activeMedia.src
        : "none";

  useEffect(() => {
    if (!open) return;

    setShowSideGallery(false);
    setIsPlaying(true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    lenis?.stop();
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      lenis?.start();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, lenis]);

  useEffect(() => {
    if (!open) return;
    setIsPlaying(true);
  }, [open, activeMediaKey]);

  const togglePlayback = () => {
    setIsPlaying((current) => {
      const next = !current;

      if (activeMedia?.type === "video") {
        const video = videoElementRef.current;
        if (video) {
          if (next) {
            void video.play();
          } else {
            video.pause();
          }
        }
      }

      return next;
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="relative flex h-[min(92vh,920px)] w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-[#0f120e] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-full w-full flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white/75">
            <div className="min-w-0 truncate pr-2 text-xs font-semibold uppercase tracking-[0.2em]">
              {activeItem?.title ?? title}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={togglePlayback}
                className="inline-flex h-9 min-w-24 items-center justify-center gap-2 rounded-full bg-white/10 px-3 text-xs font-semibold uppercase tracking-[0.18em] transition hover:bg-white/20"
                aria-label={isPlaying ? "Pause video" : "Play video"}
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-4 w-4" aria-hidden="true" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-current" aria-hidden="true" />
                    Play
                  </>
                )}
              </button>

              {galleryItems.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowSideGallery((current) => !current)}
                  className={[
                    "inline-flex h-9 w-9 items-center justify-center rounded-full border transition",
                    showSideGallery
                      ? "border-[#c38148] bg-[#c38148]/20 text-[#f0d1ad]"
                      : "border-[#c38148]/40 bg-[#c38148]/10 text-[#f0d1ad] hover:bg-[#c38148]/20",
                  ].join(" ")}
                  aria-label={showSideGallery ? "Hide gallery" : "Show gallery"}
                  aria-pressed={showSideGallery}
                >
                  <Grid2X2 className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : null}

              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-[#b46c29]"
                aria-label="Close video"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div
            className={[
              "grid min-h-0 flex-1 overflow-hidden",
              showSideGallery
                ? "lg:grid-cols-[minmax(0,1fr)_280px]"
                : "grid-cols-1",
            ].join(" ")}
          >
            <div className="relative min-h-0 bg-black">
              {activeMedia?.type === "youtube" ? (
                <YouTubeEmbed
                  videoId={activeMedia.videoId}
                  title={activeItem?.title ?? title}
                  isPlaying={isPlaying}
                />
              ) : activeMedia?.type === "video" ? (
                <video
                  ref={videoElementRef}
                  key={activeMedia.src}
                  src={activeMedia.src}
                  className="absolute inset-0 h-full w-full"
                  controls
                  autoPlay
                  playsInline
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="flex h-full min-h-[50vh] w-full items-center justify-center px-6 text-center">
                  <p className="font-heading text-base text-white/80 sm:text-lg">
                    Video link coming soon for {activeItem?.title ?? title}.
                  </p>
                </div>
              )}
            </div>

            {showSideGallery ? (
              <aside className="border-t border-white/10 bg-[#151912] p-3 lg:border-t-0 lg:border-l">
                <div className="mb-3 flex items-center justify-between text-white/60">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Gallery
                  </span>
                  <Grid2X2 className="h-4 w-4" aria-hidden="true" />
                </div>
                <div
                  className="grid max-h-full grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 lg:grid-cols-1"
                  data-lenis-prevent
                >
                  {galleryItems.map((item, index) => (
                    <button
                      key={`${item.title}-${index}`}
                      type="button"
                      onClick={() => {
                        onSelectGalleryItem?.(index);
                        setIsPlaying(true);
                      }}
                      className={[
                        "group relative overflow-hidden rounded-xl border transition",
                        item.isActive
                          ? "border-[#c38148] ring-2 ring-[#c38148]/30"
                          : "border-white/10 hover:border-white/25",
                      ].join(" ")}
                      aria-label={`Open ${item.title}`}
                    >
                      <div className="relative aspect-[16/10] bg-black">
                        <Image
                          src={item.imageSrc}
                          alt={item.imageAlt}
                          fill
                          sizes="(max-width: 1024px) 33vw, 220px"
                          className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2 py-2 text-[11px] font-semibold text-white">
                          <span className="line-clamp-1">{item.title}</span>
                          <ChevronRight
                            className="h-4 w-4 shrink-0"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
